import { defineStore } from 'pinia';
import { useStorage } from '@vueuse/core';
import { jwtDecode } from 'jwt-decode';
import {
  loginOrganisation,
  fetchOrganisationUserInfo,
  fetchMyOrganisations,
  setSelectedOrganisationId,
  ORG_STORAGE_KEY,
} from '@/api/organisation';
import { message } from 'ant-design-vue';
import { analytics } from '@/observability/analytics';
import { EVENTS } from '@/observability/events';
import {
  identifyOrganisationUser,
  resetOrganisationIdentity,
} from '@/observability/orgIdentity';

export const useAuthOrganisationStore = defineStore('authOrganisation', {
  state: () => ({
    user: null,
    // Organisations the caller may run the panel for, each with the role they
    // hold there. A role is per organisation, so this cannot collapse to one.
    organisations: [],
    selectedOrganisationId: useStorage(ORG_STORAGE_KEY, ''),
    accessToken: useStorage('org_access_token', ''),
    refreshToken: useStorage('org_refresh_token', ''),
    isfetchUserLoading: false,
  }),
  getters: {
    userInitials: (state) => {
      if (!state.user) return '';
      const { full_name } = state.user;
      if (!full_name) return '';
      const parts = full_name.split(' ');
      const firstName = parts[0];
      const lastName = parts[parts.length - 1];
      return (firstName?.charAt(0) || '') + (lastName?.charAt(0) || '');
    },
    isAuthenticated: (state) => {
      if (state.accessToken && state.refreshToken) {
        try {
          return jwtDecode(state.accessToken).exp > Date.now() / 1000;
        } catch (e) {
          return false;
        }
      }
      return false;
    },
    currentOrganisation: (state) =>
      state.organisations.find(
        (org) => org.uuid === state.selectedOrganisationId
      ) || null,
    // Role in the organisation currently being acted on - never a global role.
    currentRole() {
      return this.currentOrganisation?.role?.toLowerCase() || null;
    },
    isOrgAdmin() {
      return this.currentRole === 'admin';
    },
    isOrgTeacher() {
      return this.currentRole === 'teacher';
    },
    currentOrganisationId: (state) => state.selectedOrganisationId || '',
    currentOrganisationCode() {
      return this.currentOrganisation?.code || '';
    },
    needsOrganisationChoice: (state) =>
      state.organisations.length > 1 && !state.selectedOrganisationId,
  },
  actions: {
    setOrganisations(list) {
      this.organisations = list || [];
      const stillValid = this.organisations.some(
        (org) => org.uuid === this.selectedOrganisationId
      );
      if (!stillValid) {
        // Auto-select only when there is no genuine choice to make; otherwise
        // leave it unset so the UI prompts for an organisation.
        this.selectOrganisation(
          this.organisations.length === 1 ? this.organisations[0].uuid : ''
        );
      }
    },

    selectOrganisation(uuid) {
      this.selectedOrganisationId = uuid || '';
      setSelectedOrganisationId(uuid);
      // The only place the acting organisation changes, so analytics context
      // is refreshed here rather than from a watcher. Role is per organisation
      // - a teacher at one school may be an administrator at another - so the
      // group and the role properties both have to follow the switch.
      this.syncAnalyticsIdentity();
    },

    /**
     * Push current panel identity, role context and group at analytics.
     * Idempotent, and a no-op until a user is loaded, so every seam that can
     * change any of its inputs can call it without checking first.
     */
    syncAnalyticsIdentity() {
      identifyOrganisationUser({
        user: this.user,
        organisations: this.organisations,
        selectedOrganisationId: this.selectedOrganisationId,
      });
    },

    async loginOrganisation(credentials) {
      const { data, status } = await loginOrganisation(credentials);
      if (status === 200 || status === 201) {
        this.accessToken = data.access;
        this.refreshToken = data.refresh;
        this.user = data.user;
        this.setOrganisations(data.organisations);
        if (data.default_organisation_uuid) {
          this.selectOrganisation(data.default_organisation_uuid);
        }
        // Covers the multi-school case, where no default is returned and
        // selectOrganisation is therefore never called with a real uuid.
        this.syncAnalyticsIdentity();
        // Emitted AFTER identify, so the login belongs to the person who just
        // logged in rather than to whoever held the browser before them.
        // First login is the first occurrence of this per person, so the
        // invited-staff funnel needs no separate event for it.
        analytics.track(EVENTS.ORG_PANEL_LOGGED_IN);
      }
      return { data, status };
    },

    async fetchOrganisationUser() {
      if (this.isfetchUserLoading) return;
      this.isfetchUserLoading = true;
      try {
        // A reload has no login response to read, so the organisation list is
        // refetched rather than trusted from storage.
        if (!this.organisations.length) {
          const { data, status } = await fetchMyOrganisations(this.accessToken);
          if (status === 200) this.setOrganisations(data);
        }
        if (this.user?.organisation) {
          return;
        }
        const { data, status } = await fetchOrganisationUserInfo(
          this.accessToken
        );
        if (status === 200) {
          this.user = data;
          // Mirrors the student store's fetchUser seam: re-derived on every
          // fetch so a role change mid-session stops tagging events with
          // stale context. This is also the seam that identifies a teacher
          // who arrives already authenticated, with no login to hook.
          this.syncAnalyticsIdentity();
        }
        return { data, status };
      } catch (error) {
        return { error };
      } finally {
        this.isfetchUserLoading = false;
      }
    },

    logoutOrganisation(callback) {
      localStorage.removeItem('org_access_token');
      localStorage.removeItem('org_refresh_token');
      this.user = null;
      this.organisations = [];
      this.selectOrganisation('');
      this.accessToken = '';
      this.refreshToken = '';
      resetOrganisationIdentity();
      message.success('Logged out successfully!');
      callback && callback();
    },
  },
});
